import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {ModalInterface} from '../../../shared/modal/modal.interface';
import {ModalStateEvent} from '../../../shared/modal/modal-state-event';
import {ModalState} from '../../../shared/modal/modal-state.enum';
import {ProjectCollaborationDataProviderService} from './project-collaboration.data-provider.service';
import {ResponseNotificationService} from '../../../core/services/response-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {CollaborationTypeEnum, SERVICE_COLLABORATION_TYPES} from '../../../core/enums/collaboration-type.enum';

@Component({
  selector: 'app-collaboration-edit',
  templateUrl: './collaboration-edit.component.html',
  styleUrls: ['./collaboration-edit.component.scss']
})
export class CollaborationEditComponent implements OnInit, ModalInterface {
  public data: any;
  public subject: Subject<ModalStateEvent>;
  public state: Observable<ModalStateEvent>;
  public form: UntypedFormGroup;
  public isLoading = false;
  public readonly serviceCollaborationTypes = SERVICE_COLLABORATION_TYPES;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly collaborationDPS: ProjectCollaborationDataProviderService,
    private readonly notifications: ResponseNotificationService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    const collaboration = this.data?.collaboration;
    const hasHistoryPost = !!collaboration?.historyPost;
    this.form = this.fb.group({
      title: new UntypedFormControl(collaboration?.title || '', [Validators.required, Validators.maxLength(255)]),
      shortDescription: new UntypedFormControl(collaboration?.shortDescription || '', [Validators.maxLength(255)]),
      description: new UntypedFormControl(collaboration?.description || '', [Validators.maxLength(10000)]),
      serviceSummary: new UntypedFormControl(collaboration?.serviceSummary || '', [Validators.maxLength(5000)]),
      createHistoryEntryOnApproval: new UntypedFormControl(
        hasHistoryPost ? true : !!collaboration?.createHistoryEntryOnApproval
      ),
      historyStartDate: new UntypedFormControl(collaboration?.historyStartDate ? new Date(collaboration.historyStartDate) : null),
      historyEndDate: new UntypedFormControl(collaboration?.historyEndDate ? new Date(collaboration.historyEndDate) : null),
    });

    if (this.isHistoryCheckboxLocked()) {
      this.form.get('createHistoryEntryOnApproval')?.disable({emitEvent: false});
    }

    this.subject.next(new ModalStateEvent(ModalState.VALID));
    this.state.subscribe((mse: ModalStateEvent) => {
      if (mse.status === ModalState.PENDING) {
        this.submit();
      }
    });
  }

  public submit(): void {
    const collaborationId = this.data?.collaboration?.id;
    if (!collaborationId || this.form.invalid || this.isLoading) {
      return;
    }

    const serviceSummary = (this.form.get('serviceSummary')?.value || '').trim();
    const createHistoryEntryOnApprovalControl = this.form.get('createHistoryEntryOnApproval');
    const historyStartDate = this.form.get('historyStartDate')?.value || null;
    const historyEndDate = this.form.get('historyEndDate')?.value || null;

    this.isLoading = true;
    this.subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.collaborationDPS.update(collaborationId, {
      title: (this.form.value.title || '').trim(),
      shortDescription: (this.form.value.shortDescription || '').trim() || null,
      description: (this.form.value.description || '').trim() || null,
      serviceSummary: this.isServiceType() ? (serviceSummary || null) : null,
      createHistoryEntryOnApproval: this.isServiceType() ? !!createHistoryEntryOnApprovalControl?.value : null,
      historyStartDate: this.isServiceType() ? historyStartDate : null,
      historyEndDate: this.isServiceType() ? historyEndDate : null,
    }).subscribe({
      next: (collaboration) => {
        this.subject.next(new ModalStateEvent(ModalState.SUCCESS, collaboration));
      },
      error: () => {
        this.notifications.emitMessage(this.translate.instant('features.user.collaboration.detail.error_review'));
        this.subject.next(new ModalStateEvent(ModalState.VALID));
      }
    }).add(() => {
      this.isLoading = false;
      this.subject.next(new ModalStateEvent(ModalState.PROCESS_END));
    });
  }

  public isServiceType(): boolean {
    const type = this.data?.collaboration?.type;
    return !!type && this.serviceCollaborationTypes.includes(type as CollaborationTypeEnum);
  }

  public getTypeLabel(): string {
    const type = this.data?.collaboration?.type;
    if (!type) {
      return '';
    }

    return this.translate.instant('features.user.collaboration.types.' + type);
  }

  public isHistoryCheckboxLocked(): boolean {
    return this.isServiceType() && (!!this.data?.collaboration?.historyPost || this.hasMediaPosts());
  }

  private hasMediaPosts(): boolean {
    const assets = this.data?.collaboration?.assets || [];
    return assets.some((asset) => !!asset?.mediaPost);
  }
}
